import { useMemo, useState } from 'react';
import { Alert, Button, ButtonGroup, Card, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ActivityTimeline from './ActivityTimeline';
import { useFactoryEvents } from '../../features/factory-events/hooks';

/**
 * Everything that ever happened to one product or one order, on its own page.
 *
 * The factory-wide pages answer "what happened on the 12th". This answers "what
 * happened to THIS one", which is the question actually asked when a customer
 * rings up about their cable — and it is the same event history, filtered, so
 * there is nothing to keep in step.
 *
 * Pass exactly one of `jobId` / `orderId`.
 */

const KINDS = [
  { key: '', label: 'All' },
  { key: 'activity', label: 'Decisions' },
  { key: 'report', label: 'Production' }
];

const PAGE_SIZE = 50;

const EntityEventTimeline = ({
  jobId,
  orderId,
  title = 'History',
  subtitle,
  height = 460
}) => {
  const [kind, setKind] = useState('');
  const [page, setPage] = useState(1);

  const params = useMemo(() => {
    const p = { page, limit: PAGE_SIZE };
    if (kind) p.kind = kind;
    if (jobId) p.jobId = jobId;
    if (orderId) p.orderId = orderId;
    return p;
  }, [kind, page, jobId, orderId]);

  // Nothing to ask for until we know which one — the id arrives a render late
  // on both pages, and asking without it would fetch the whole factory.
  const enabled = Boolean(jobId || orderId);
  const { data, isLoading, isError, error } = useFactoryEvents(enabled ? params : { skip: true });

  if (!enabled) return null;

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const pages = data?.pages ?? 1;

  const apiError =
    error?.response?.data?.error || error?.message || 'Could not load the history';

  return (
    <Card className="h-100">
      <Card.Body>
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-2">
          <div>
            <h3 className="text-1100 mb-1">{title}</h3>
            {subtitle && <p className="text-700 mb-0 fs-9">{subtitle}</p>}
          </div>

          <div className="d-flex align-items-center gap-2">
            <ButtonGroup size="sm">
              {KINDS.map(k => (
                <Button
                  key={k.key || 'all'}
                  variant={kind === k.key ? 'primary' : 'phoenix-secondary'}
                  onClick={() => {
                    setKind(k.key);
                    setPage(1);
                  }}
                >
                  {k.label}
                </Button>
              ))}
            </ButtonGroup>
            <Button
              as={Link}
              to={`/apps/activity`}
              variant="link"
              size="sm"
              className="px-1"
              title="The whole factory history"
            >
              All events
            </Button>
          </div>
        </div>

        {isError ? (
          <Alert variant="danger" className="mb-0 py-2">
            {apiError}
          </Alert>
        ) : isLoading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" size="sm" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-5 text-700">
            Nothing recorded here yet.
          </div>
        ) : (
          <>
            <div className="scrollbar" style={{ maxHeight: height, overflowX: 'hidden' }}>
              <ActivityTimeline reports={rows} />
            </div>

            <div className="d-flex align-items-center justify-content-between mt-3">
              <span className="text-700 fs-9">
                {rows.length} of {total.toLocaleString()}
              </span>
              {pages > 1 && (
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="phoenix-secondary"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                  >
                    Newer
                  </Button>
                  <span className="text-700 fs-9">
                    {page}/{pages}
                  </span>
                  <Button
                    variant="phoenix-secondary"
                    size="sm"
                    disabled={page >= pages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Older
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default EntityEventTimeline;
